import { useCallback, useEffect, useState } from 'react';
import { RequireBot } from '../components/ChooseBot';
import { useBot } from '../contexts/BotContext';
import {
  hubClient,
  type EvalResult,
  type EvalRun,
  type EvalRunSummary,
  type EvalSuggestion,
} from '../services/hubClient';

type Tab = 'runs' | 'results' | 'suggestions';

const verdictColor: Record<string, string> = {
  pass: 'bg-green-100 text-green-800',
  fail: 'bg-red-100 text-red-800',
  partial: 'bg-amber-100 text-amber-800',
};
const statusColor: Record<string, string> = {
  proposed: 'bg-blue-100 text-blue-800',
  applied: 'bg-green-100 text-green-800',
  auto_applied: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-gray-100 text-gray-600',
  rolled_back: 'bg-amber-100 text-amber-800',
};

function metricFromAggregate(agg: Record<string, unknown> | null | undefined): string {
  if (!agg) return '—';
  try {
    return JSON.stringify(agg).slice(0, 80);
  } catch {
    return '—';
  }
}

function RunsTab({ system, botId }: { system: string; botId: string }) {
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [summary, setSummary] = useState<EvalRunSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, s] = await Promise.all([
        hubClient.listEvalRuns({ system, botId, limit: 50 }),
        hubClient.getEvalRunSummary(system, botId).catch(() => null),
      ]);
      setRuns(r);
      setSummary(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }, [system, botId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function trigger() {
    setTriggering(true);
    setError(null);
    try {
      const { jobId } = await hubClient.triggerEvalRun({ system, botId, kind: 'eval' });
      alert(`Eval run queued: job ${jobId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to trigger run');
    } finally {
      setTriggering(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        {summary?.latest ? (
          <div className="text-sm text-gray-600">
            Latest: <span className="font-medium">{summary.metric ?? 'metric'}</span>{' '}
            {summary.delta != null && (
              <span className={summary.regressed ? 'text-red-600' : 'text-green-600'}>
                Δ {summary.delta > 0 ? '+' : ''}{summary.delta.toFixed(3)} {summary.regressed ? '(regressed)' : ''}
              </span>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-400">No prior runs.</div>
        )}
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="rounded border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40">Refresh</button>
          <button onClick={trigger} disabled={triggering} className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-40">
            {triggering ? 'Queuing…' : 'Trigger eval run'}
          </button>
        </div>
      </div>
      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>{['When', 'Layer', 'Status', 'Aggregate', 'Trigger'].map((h) => <th key={h} className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {runs.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{new Date(r.createdAt).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{r.layer}</td>
                <td className="px-3 py-2 text-xs">{r.status}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-gray-500">{metricFromAggregate(r.aggregate)}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{r.triggeredBy ?? '—'}</td>
              </tr>
            ))}
            {!loading && runs.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400">No runs.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResultsTab({ system, botId }: { system: string; botId: string }) {
  const [verdict, setVerdict] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [results, setResults] = useState<EvalResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResults(await hubClient.listEvalResults({ system, botId, verdict: verdict || undefined, diagnosis: diagnosis || undefined, limit: 100 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [system, botId, verdict, diagnosis]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <select value={verdict} onChange={(e) => setVerdict(e.target.value)} className="rounded border border-gray-300 px-3 py-2 text-sm">
          <option value="">all verdicts</option>
          {['pass', 'fail', 'partial'].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} className="rounded border border-gray-300 px-3 py-2 text-sm">
          <option value="">all diagnoses</option>
          {['retrieval_miss', 'data_gap', 'hallucination', 'prompt'].map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>{['When', 'Layer', 'Verdict', 'Score', 'Diagnosis'].map((h) => <th key={h} className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {results.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{new Date(r.createdAt).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{r.layer}</td>
                <td className="px-3 py-2">{r.verdict && <span className={`rounded px-1.5 py-0.5 text-[11px] ${verdictColor[r.verdict] ?? ''}`}>{r.verdict}</span>}</td>
                <td className="px-3 py-2 text-xs">{r.score != null ? r.score.toFixed(2) : '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{r.diagnosis ?? '—'}</td>
              </tr>
            ))}
            {!loading && results.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400">No results.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SuggestionsTab({ system, botId }: { system: string; botId: string }) {
  const [items, setItems] = useState<EvalSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await hubClient.listEvalSuggestions({ system, botId, limit: 50 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, [system, botId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function apply(s: EvalSuggestion) {
    if (!confirm('Apply this suggestion (creates a FAQ document)?')) return;
    try {
      await hubClient.applyEvalSuggestion(s.id, { actor: 'human:hub-ui' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to apply');
    }
  }
  async function rollback(s: EvalSuggestion) {
    if (!confirm('Roll back this applied suggestion?')) return;
    try {
      await hubClient.rollbackEvalSuggestion(s.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to roll back');
    }
  }

  return (
    <div className="space-y-4">
      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="space-y-2">
        {items.map((s) => {
          const applied = s.status === 'applied' || s.status === 'auto_applied';
          return (
            <div key={s.id} className="rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`rounded px-1.5 py-0.5 text-[11px] ${statusColor[s.status] ?? 'bg-gray-100'}`}>{s.status}</span>
                  <span className="text-sm font-medium">{s.kind}</span>
                  <span className="text-[11px] text-gray-400">{s.riskClass}{s.confidence != null ? ` · conf ${s.confidence.toFixed(2)}` : ''}</span>
                </div>
                <div className="flex gap-2">
                  {s.status === 'proposed' && <button onClick={() => apply(s)} className="rounded bg-blue-600 px-3 py-1 text-xs text-white">Apply</button>}
                  {applied && <button onClick={() => rollback(s)} className="rounded border border-gray-300 px-3 py-1 text-xs">Roll back</button>}
                </div>
              </div>
              <pre className="mt-2 max-h-32 overflow-auto rounded bg-gray-50 p-2 text-[11px] text-gray-600">{JSON.stringify(s.proposal, null, 2)}</pre>
            </div>
          );
        })}
        {!loading && items.length === 0 && <p className="py-6 text-center text-sm text-gray-400">No suggestions.</p>}
      </div>
    </div>
  );
}

function EvalPage() {
  const { system, botId } = useBot();
  const [tab, setTab] = useState<Tab>('runs');

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900">Eval</h1>
        <p className="mb-6 text-gray-600">
          Offline evaluation for {system}/{botId}: runs &amp; aggregates, per-case results with diagnoses, and the
          amendment suggestion queue (apply / roll back additive FAQ fixes).
        </p>
        <div className="mb-6 flex gap-2 border-b border-gray-200">
          {([['runs', 'Runs'], ['results', 'Results'], ['suggestions', 'Suggestions']] as [Tab, string][]).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`-mb-px rounded-t-lg px-4 py-2 text-sm font-medium ${tab === id ? 'border border-b-0 border-gray-200 bg-white text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === 'runs' && <RunsTab system={system} botId={botId} />}
        {tab === 'results' && <ResultsTab system={system} botId={botId} />}
        {tab === 'suggestions' && <SuggestionsTab system={system} botId={botId} />}
      </div>
    </div>
  );
}

export default Eval;

/** A1: renders only once a bot is chosen (no default bot). */
export function Eval() {
  return (
    <RequireBot title="Eval">
      <EvalPage />
    </RequireBot>
  );
}
